/**
 * useSpeechToText — Free, on-device dictation via the Web Speech API.
 * No API key, no backend, no cost. Feature-detected; consumers should hide UI when unsupported.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

type SpeechRecognitionCtor = new () => any;

const getRecognitionCtor = (): SpeechRecognitionCtor | null => {
  if (typeof window === 'undefined') return null;
  const w = window as any;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
};

interface UseSpeechToTextOptions {
  lang?: string;
  onFinal?: (text: string) => void;
}

export function useSpeechToText(options: UseSpeechToTextOptions = {}) {
  const { lang, onFinal } = options;
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<any>(null);
  const onFinalRef = useRef(onFinal);
  onFinalRef.current = onFinal;

  const isSupported = typeof window !== 'undefined' && !!getRecognitionCtor();

  const stop = useCallback(() => {
    try {
      recognitionRef.current?.stop();
    } catch {
      // ignore
    }
  }, []);

  const reset = useCallback(() => setTranscript(''), []);

  const start = useCallback(() => {
    const Ctor = getRecognitionCtor();
    if (!Ctor) {
      setError('Voice input is not supported in this browser.');
      return;
    }
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
    }

    const rec = new Ctor();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = lang || (typeof navigator !== 'undefined' ? navigator.language : 'en-US') || 'en-US';

    setError(null);
    setTranscript('');

    rec.onstart = () => setIsListening(true);
    rec.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };
    rec.onerror = (e: any) => {
      const code = e?.error;
      const friendly =
        code === 'not-allowed' || code === 'service-not-allowed'
          ? 'Microphone permission denied. Enable mic access to dictate.'
          : code === 'no-speech'
          ? "Didn't catch that — try again."
          : code === 'audio-capture'
          ? 'No microphone found.'
          : 'Voice input failed. Please try again.';
      setError(friendly);
      setIsListening(false);
    };
    rec.onresult = (event: any) => {
      let interim = '';
      let finalText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) finalText += r[0].transcript;
        else interim += r[0].transcript;
      }
      const combined = (finalText || interim).trim();
      if (combined) setTranscript(combined);
      if (finalText && onFinalRef.current) {
        onFinalRef.current(finalText.trim());
      }
    };

    recognitionRef.current = rec;
    try {
      rec.start();
    } catch (e) {
      setError('Could not start voice input.');
      setIsListening(false);
    }
  }, [lang]);

  useEffect(() => {
    return () => {
      try { recognitionRef.current?.stop(); } catch {}
    };
  }, []);

  return { isSupported, isListening, transcript, error, start, stop, reset };
}
